import { FaroTooltip } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Box } from "@mui/material";
import {
  DateCalendar,
  LocalizationProvider,
  PickerDay,
  PickerDayProps,
  PickersCalendarHeaderProps,
} from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { createContext, useContext, useMemo, useRef, type JSX } from "react";
import { CalendarHeader } from "./calendar-header";
import {
  CALENDAR_SX,
  OUTSIDE_RANGE,
  bandSx,
  cellSx,
  rangeRoleOf,
} from "./calendar-styles";
import {
  PageTurnOnRest,
  captureLabel,
  releaseTouchCapture,
  useBucketCounts,
  usePageTurnOnRest,
} from "./calendar-utils";
import {
  Bounds,
  CaptureGranularity,
  ViewProps,
} from "./capture-calendar-types";
import { CaptureDot } from "./capture-markers";

/** What MUI's day and header slots need, as MUI renders them without our props */
type DayContextValue = Pick<
  ViewProps,
  | "startDate"
  | "endDate"
  | "bounds"
  | "onToggleView"
  | "onHover"
  | "onPress"
  | "isDragging"
  | "onMonthChange"
> & {
  /** Capture count per day, keyed by the day's start in millis */
  counts: Map<number, number>;
  /** Turns the page when a drag rests on the month's first or last day, or a filler */
  pageTurn: PageTurnOnRest;
};

const DayContext = createContext<DayContextValue | undefined>(undefined);

/** @returns The values DayCalendar provides to its slots */
function useDayContext(): DayContextValue {
  const context = useContext(DayContext);
  assert(context, "DayContext is only available inside DayCalendar");
  return context;
}

/** @returns MUI's DateCalendar, with a dot on every day holding captures */
export function DayCalendar({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  onPress,
  isDragging,
  isPickable,
  month,
  onMonthChange,
  bounds,
  onToggleView,
}: ViewProps): JSX.Element {
  const counts = useBucketCounts(stats.days);
  // DateCalendar's onChange carries no event, so the modifier is caught on the way down
  const isShiftHeld = useRef(false);
  const pageTurn = usePageTurnOnRest(isDragging);
  const context = useMemo(
    () => ({
      counts,
      startDate,
      endDate,
      bounds,
      onToggleView,
      onHover,
      onPress,
      isDragging,
      onMonthChange,
      pageTurn,
    }),
    [
      counts,
      startDate,
      endDate,
      bounds,
      onToggleView,
      onHover,
      onPress,
      isDragging,
      onMonthChange,
      pageTurn,
    ],
  );

  return (
    <DayContext value={context}>
      <Box
        component="div"
        onClickCapture={(event) => {
          isShiftHeld.current = event.shiftKey;
        }}
        onPointerDown={releaseTouchCapture}
        // A finger drags a range rather than scrolling the page
        sx={{ touchAction: "none" }}
      >
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateCalendar
            // DateCalendar reads its month on mount only, so a page turned by a drag remounts it
            key={month.startOf("month").toMillis()}
            views={["day"]}
            reduceAnimations
            // The range is drawn by the day slot, so MUI holds no selection of its own
            value={null}
            referenceDate={month}
            minDate={bounds.min}
            maxDate={bounds.max}
            shouldDisableDate={(day: DateTime) => !isPickable(day, "day")}
            slots={{ day: CaptureDay, calendarHeader: DayCalendarHeader }}
            onMonthChange={onMonthChange}
            onChange={(picked: DateTime | null) =>
              picked && onPick(picked, "day", isShiftHeld.current)
            }
            sx={CALENDAR_SX}
          />
        </LocalizationProvider>
      </Box>
    </DayContext>
  );
}

/**
 * @returns The day view's header, in MUI's calendarHeader slot
 * @param props The header props DateCalendar provides
 */
function DayCalendarHeader({
  currentMonth,
  onMonthChange,
  labelId,
}: PickersCalendarHeaderProps): JSX.Element {
  const { bounds, onToggleView, isDragging } = useDayContext();
  const shown = currentMonth.startOf("month");

  return (
    <CalendarHeader
      label={currentMonth.toFormat("LLLL yyyy")}
      labelId={labelId}
      granularity={CaptureGranularity.day}
      onToggleView={onToggleView}
      onPrevious={() => onMonthChange(currentMonth.minus({ months: 1 }))}
      onNext={() => onMonthChange(currentMonth.plus({ months: 1 }))}
      isPreviousDisabled={shown <= bounds.min.startOf("month")}
      isNextDisabled={shown >= bounds.max.startOf("month")}
      isDragging={isDragging}
    />
  );
}

/**
 * @returns One day of the calendar, dotted when it holds captures
 * @param props The day props MUI's DayCalendar provides
 */
function CaptureDay({ day, today, ...rest }: PickerDayProps): JSX.Element {
  const {
    counts,
    startDate,
    endDate,
    onHover,
    onPress,
    bounds,
    onMonthChange,
    pageTurn,
  } = useDayContext();

  const count = counts.get(day.startOf("day").toMillis()) ?? 0;
  const role = rangeRoleOf(day, "day", startDate, endDate);
  const weekOptions = { useLocaleWeeks: true };
  const isRowStart = day.hasSame(day.startOf("week", weekOptions), "day");
  const isRowEnd = day.hasSame(day.endOf("week", weekOptions), "day");
  const turnTo = pageEdgeTarget(day, !!rest.outsideCurrentMonth, bounds);
  function restOnEdge(): void {
    if (turnTo) pageTurn.rest(() => onMonthChange(turnTo));
  }

  // Banding the neighbouring months' empty fillers keeps a range spanning months continuous
  if (rest.outsideCurrentMonth) {
    const band = role.isSelected
      ? { ...OUTSIDE_RANGE, isSelected: true }
      : role;
    return (
      <Box
        component="span"
        onPointerEnter={restOnEdge}
        onPointerLeave={pageTurn.leave}
        sx={bandSx(
          band,
          isRowStart || role.isRangeStart,
          isRowEnd || role.isRangeEnd,
        )}
      >
        <PickerDay {...rest} day={day} today={today} />
      </Box>
    );
  }

  return (
    <FaroTooltip
      placement="top"
      title={`${day.toFormat("d LLL yyyy")} — ${captureLabel(count)}`}
      sx={bandSx(role, isRowStart, isRowEnd)}
    >
      <PickerDay
        {...rest}
        day={day}
        today={today}
        selected={role.isSelected}
        // The ring is drawn by cellSx; MUI's own outline would double it
        disableHighlightToday
        aria-label={`${day.toISODate()} ${captureLabel(count)}`}
        onPointerEnter={() => {
          onHover?.([day, "day"]);
          restOnEdge();
        }}
        onPointerLeave={() => {
          onHover?.();
          pageTurn.leave();
        }}
        onPointerDown={() => onPress?.([day, "day"])}
        sx={cellSx(role, !!today, !!rest.disabled)}
      >
        {day.toFormat("d")}
        {count > 0 && <CaptureDot />}
      </PickerDay>
    </FaroTooltip>
  );
}

/**
 * @returns The month a drag resting on the day turns to: the filler's own month, or the
 * neighbouring one from the month's first or last day; none for other days or past the bounds
 * @param day The day under the pointer
 * @param isFiller True for a neighbouring month's day shown to fill the grid
 * @param bounds The span the calendar may be paged within
 */
function pageEdgeTarget(
  day: DateTime,
  isFiller: boolean,
  bounds: Bounds,
): DateTime | undefined {
  const target = isFiller ? day : monthStepFrom(day);
  const month = target?.startOf("month");
  const isInBounds =
    !!month && month >= bounds.min.startOf("month") && month <= bounds.max;
  return isInBounds ? target : undefined;
}

/**
 * @returns The previous month from the month's first day, the next from its last, else none
 * @param day A day of the month in sight
 */
function monthStepFrom(day: DateTime): DateTime | undefined {
  if (day.day === 1) return day.minus({ months: 1 });
  if (day.hasSame(day.endOf("month"), "day")) return day.plus({ months: 1 });
}
